/**
 * Swiper Slideshow Custom Element
 * A reusable component that automatically initializes Swiper with responsive behavior
 */
class SwiperSlideshow extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.isInitialized = false;
    this.savedSlideIndex = 0;
    this.disableOnMediaQuery = null;
    this.onDisableOnChange = null;
    this.mediaQueries = {
      mediumUp: window.matchMedia('(min-width: 768px)'),
      largeUp: window.matchMedia('(min-width: 1024px)'),
    };
  }

  connectedCallback() {
    this.waitForSwiper().then(() => {
      this.bindDisableOnListener();
      this.syncLayoutMode();
    });
  }

  disconnectedCallback() {
    this.destroySwiperInstance();
    this.unbindDisableOnListener();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.handleResizeHandler) {
      window.removeEventListener('resize', this.handleResizeHandler);
      this.handleResizeHandler = null;
    }
    if (this.desktopAutoHeightQuery && this.onDesktopAutoHeightChange) {
      this.desktopAutoHeightQuery.removeEventListener(
        'change',
        this.onDesktopAutoHeightChange
      );
    }
    this.desktopAutoHeightQuery = null;
    this.onDesktopAutoHeightChange = null;
    clearTimeout(this.layoutRefreshTimer);
    this.layoutRefreshTimer = null;
    this.isInitialized = false;
  }

  getConfig() {
    // Get all possible attributes from the element - Mobile-first approach
    const attrs = {
      // Mobile values (base attributes)
      slidesPerView: this.getAttribute('slides-per-view'),
      spaceBetween: this.getAttribute('space-between'),
      loop: this.getAttribute('loop'),
      centeredSlides: this.getAttribute('centered-slides'),
      speed: this.getAttribute('speed'),
      effect: this.getAttribute('effect'),
      direction: this.getAttribute('direction'),
      autoplay: this.getAttribute('autoplay'),
      autoplayDelay: this.getAttribute('autoplay-delay'),
      freeMode: this.getAttribute('free-mode'),
      slidesOffsetBefore: this.getAttribute('slides-offset-before'),

      // Desktop overrides (desktop-* prefixed attributes)
      desktopSlidesPerView: this.getAttribute('desktop-slides-per-view'),
      desktopSpaceBetween: this.getAttribute('desktop-space-between'),
      desktopLoop: this.getAttribute('desktop-loop'),
      desktopCenteredSlides: this.getAttribute('desktop-centered-slides'),
      desktopDirection: this.getAttribute('desktop-direction'),
      desktopAutoplay: this.getAttribute('desktop-autoplay'),
      desktopAutoplayDelay: this.getAttribute('desktop-autoplay-delay'),
      desktopFreeMode: this.getAttribute('desktop-free-mode'),
      desktopSlidesOffsetBefore: this.getAttribute(
        'desktop-slides-offset-before'
      ),

      // Navigation
      showNavigation: this.getAttribute('show-navigation'),

      // Pagination
      pagination: this.getAttribute('pagination'),
      showPagination: this.getAttribute('show-pagination'),
      numberPagination: this.getAttribute('number-pagination'),

      // Advanced features
      grabCursor: this.getAttribute('grab-cursor'),
      allowTouchMove: this.getAttribute('allow-touch-move'),
      autoHeight: this.getAttribute('auto-height'),
      desktopAutoHeight: this.getAttribute('desktop-auto-height'),
      touchStartPreventDefault: this.getAttribute(
        'touch-start-prevent-default'
      ),
      mousewheel: this.getAttribute('mousewheel'),

      // Scrollbar
      showScrollbar: this.getAttribute('show-scrollbar'),
    };

    // Set up base configuration using mobile values as defaults
    const effect = attrs.effect || 'slide';
    const config = {
      // Basic settings - mobile values as base
      slidesPerView: attrs.slidesPerView || 1,
      spaceBetween: parseInt(attrs.spaceBetween || 20),
      loop: attrs.loop === 'true' || false,
      centeredSlides: attrs.centeredSlides === 'true' || false,
      speed: parseInt(attrs.speed || 400),
      effect,
      direction: attrs.direction || 'horizontal',
      freeMode: attrs.freeMode === 'true' || false,
      slidesOffsetBefore: parseInt(attrs.slidesOffsetBefore || 0),

      // Navigation - Update to use our custom navigation classes
      navigation:
        attrs.showNavigation === 'true'
          ? {
              nextEl: '.swiper-slideshow__nav-button--next',
              prevEl: '.swiper-slideshow__nav-button--prev',
              lockClass: 'swiper-slideshow__nav-button--lock',
              disabledClass: 'swiper-slideshow__nav-button--disabled',
            }
          : false,

      // Pagination
      pagination:
        attrs.pagination === 'true' || attrs.showPagination === 'true'
          ? {
              el: '.swiper-slideshow__pagination',
              clickable: true,
              type: attrs.numberPagination === 'true' ? 'fraction' : 'bullets',
              renderBullet:
                attrs.numberPagination === 'true'
                  ? (index, className) =>
                      `<span class="${className}">0${index + 1}</span>`
                  : undefined,
            }
          : false,

      // Scrollbar
      scrollbar:
        attrs.showScrollbar === 'true'
          ? {
              el: '.swiper-scrollbar',
              draggable: false,
              dragSize: 'auto',
              hide: false,
            }
          : false,

      // Advanced features
      grabCursor: attrs.grabCursor !== 'false',
      allowTouchMove: attrs.allowTouchMove !== 'false',
      autoHeight:
        attrs.desktopAutoHeight === 'true'
          ? false
          : attrs.autoHeight === 'true',
      touchStartPreventDefault: attrs.touchStartPreventDefault !== 'false',
      preloadImages: true,
      watchSlidesProgress: true,

      noSwiping: true,
      noSwipingClass: 'swiper-no-swiping',

      // Mousewheel scrolling
      mousewheel:
        attrs.mousewheel === 'true'
          ? {
              enabled: true,
              forceToAxis: false,
              releaseOnEdges: true,
            }
          : false,

      // Autoplay - mobile values as base
      autoplay:
        attrs.autoplay === 'true'
          ? {
              delay: parseInt(attrs.autoplayDelay || 5000),
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false,

      // Accessibility
      a11y: {
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
        firstSlideMessage: 'This is the first slide',
        lastSlideMessage: 'This is the last slide',
      },

      ...(effect === 'fade'
        ? {
            fadeEffect: {
              crossFade: true,
            },
          }
        : {}),

      // Breakpoints for responsive design - desktop overrides at configurable breakpoint
      breakpoints: attrs.breakpoints?.value
        ? this.convertToObject(attrs.breakpoints.value)
        : {
            [this.getDesktopBreakpoint()]: {
              slidesPerView:
                attrs.desktopSlidesPerView || attrs.slidesPerView || 'auto',
              spaceBetween: parseInt(
                attrs.desktopSpaceBetween || attrs.spaceBetween || 20
              ),
              loop:
                attrs.desktopLoop === 'true' || attrs.loop === 'true' || false,
              centeredSlides:
                attrs.desktopCenteredSlides === 'true' ||
                attrs.centeredSlides === 'true' ||
                false,
              direction:
                attrs.desktopDirection || attrs.direction || 'horizontal',
              freeMode:
                attrs.desktopFreeMode === 'true' ||
                attrs.freeMode === 'true' ||
                false,
              slidesOffsetBefore: parseInt(
                attrs.desktopSlidesOffsetBefore || attrs.slidesOffsetBefore || 0
              ),
              ...(attrs.desktopAutoHeight === 'true'
                ? { autoHeight: true }
                : {}),
              autoplay:
                attrs.desktopAutoplay === 'true'
                  ? {
                      delay: parseInt(
                        attrs.desktopAutoplayDelay ||
                          attrs.autoplayDelay ||
                          5000
                      ),
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                    }
                  : attrs.autoplay === 'true'
                    ? {
                        delay: parseInt(attrs.autoplayDelay || 5000),
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                      }
                    : false,
            },
          },
    };

    return config;
  }

  getDesktopBreakpoint() {
    const breakpoint = parseInt(
      this.getAttribute('desktop-breakpoint') || '768',
      10
    );
    return Number.isFinite(breakpoint) && breakpoint > 0 ? breakpoint : 768;
  }

  async waitForSwiper() {
    if (typeof Swiper !== 'undefined') return;

    return new Promise(resolve => {
      const checkSwiper = () => {
        if (typeof Swiper !== 'undefined') {
          resolve();
        } else {
          setTimeout(checkSwiper, 50);
        }
      };
      checkSwiper();
    });
  }

  convertToObject(stringObject) {
    try {
      return JSON.parse(stringObject.replace(/'/g, '"'));
    } catch (error) {
      console.error('Error parsing breakpoints:', error);
      return {};
    }
  }

  bindDisableOnListener() {
    const disableOn = this.getAttribute('disableOn');
    if (!disableOn || !this.mediaQueries[disableOn]) return;

    this.disableOnMediaQuery = this.mediaQueries[disableOn];
    this.onDisableOnChange = () => this.syncLayoutMode();
    this.disableOnMediaQuery.addEventListener('change', this.onDisableOnChange);
  }

  unbindDisableOnListener() {
    if (this.disableOnMediaQuery && this.onDisableOnChange) {
      this.disableOnMediaQuery.removeEventListener(
        'change',
        this.onDisableOnChange
      );
    }
    this.disableOnMediaQuery = null;
    this.onDisableOnChange = null;
  }

  shouldUseDesktopLayout() {
    const disableOn = this.getAttribute('disableOn');
    return Boolean(disableOn && this.mediaQueries[disableOn]?.matches);
  }

  syncLayoutMode() {
    if (this.shouldUseDesktopLayout()) {
      this.enableDesktopLayout();
      return;
    }

    this.enableMobileSwiper();
  }

  enableDesktopLayout() {
    const sliderContainer = this.querySelector('[data-swiper-slideshow]');
    if (!sliderContainer) return;

    if (this.swiper) {
      this.savedSlideIndex = this.swiper.activeIndex;
      this.destroySwiperInstance();
    }

    this.removeGeneratedElements(sliderContainer);
    this.unwrapSwiperStructure(sliderContainer);
    if (this.getAttribute('disableOn')) {
      this.classList.add('is-desktop-layout');
      this.classList.remove('is-mobile-swiper');
    }
    this.isInitialized = false;
  }

  enableMobileSwiper() {
    const sliderContainer = this.querySelector('[data-swiper-slideshow]');
    if (!sliderContainer) return;

    if (this.getAttribute('disableOn')) {
      this.classList.remove('is-desktop-layout');
      this.classList.add('is-mobile-swiper');
    }
    this.wrapSwiperStructure(sliderContainer);

    if (this.isInitialized && this.swiper) return;

    this.isInitialized = false;
    this.initializeSwiper();
  }

  async initializeSwiper() {
    if (this.isInitialized) return;

    const sliderContainer = this.querySelector('[data-swiper-slideshow]');
    if (!sliderContainer) return;

    if (this.shouldUseDesktopLayout()) return;

    // Create navigation if needed
    if (
      this.getAttribute('create-elements') === 'true' &&
      this.getAttribute('show-navigation') === 'true'
    ) {
      this.createNavigationElements(sliderContainer);
    }

    // Create scrollbar if needed
    if (
      this.getAttribute('create-elements') === 'true' &&
      this.getAttribute('show-scrollbar') === 'true'
    ) {
      this.createScrollbarElement(sliderContainer);
    }

    // Create pagination if needed
    if (
      this.getAttribute('create-elements') === 'true' &&
      (this.getAttribute('pagination') === 'true' ||
        this.getAttribute('show-pagination') === 'true')
    ) {
      this.createPaginationElement(sliderContainer);
    }

    const config = this.getConfig();

    if (this.savedSlideIndex > 0) {
      config.initialSlide = this.savedSlideIndex;
    }

    // Observers can be disabled per-instance (e.g. Collection Expose product cards)
    const observersEnabled = this.getAttribute('observer') !== 'false';
    config.observer = observersEnabled;
    config.observeParents =
      observersEnabled && this.getAttribute('observe-parents') !== 'false';
    config.resizeObserver =
      observersEnabled &&
      this.getAttribute('swiper-resize-observer') !== 'false';
    config.updateOnWindowResize =
      this.getAttribute('update-on-window-resize') !== 'false';

    // Handle thumbnails
    const thumbnailsAttr = this.getAttribute('thumbnails');
    if (thumbnailsAttr) {
      await this.setupControlledSwiper(config, thumbnailsAttr);
    } else {
      this.swiper = new Swiper(sliderContainer, config);
    }

    // Force update after a brief delay to ensure proper sizing
    setTimeout(() => {
      if (this.swiper) {
        this.syncDesktopAutoHeight();
        this.swiper.update();
      }
    }, 100);

    this.setupEventListeners();
    this.isInitialized = true;
  }

  async setupControlledSwiper(config, thumbnailsSelector) {
    const sliderContainer = this.querySelector('[data-swiper-slideshow]');

    if (thumbnailsSelector) {
      const gallery = this.closest('media-gallery');
      const thumbnailElement = gallery
        ? gallery.querySelector(`[${thumbnailsSelector}]`)
        : document.querySelector(`[${thumbnailsSelector}]`);

      if (thumbnailElement) {
        const thumbnailSwiper =
          await this.waitForSwiperInstance(thumbnailElement);
        if (thumbnailSwiper) {
          config.thumbs = {
            swiper: thumbnailSwiper,
            multipleActiveThumbs: false,
          };
        }
      }
      this.swiper = new Swiper(sliderContainer, config);
    } else {
      // Initialize as a thumbnail slider
      this.swiper = new Swiper(sliderContainer, {
        ...config,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
      });
    }
  }

  async waitForSwiperInstance(element, timeout = 5000) {
    if (!element) return null;

    // Try to get the swiper instance immediately
    let swiper = element.swiper || element.getSwiper?.();
    if (swiper) return swiper;

    // Wait for the swiper to be initialized
    return new Promise(resolve => {
      const startedAt = Date.now();
      const checkSwiper = () => {
        swiper = element.swiper || element.getSwiper?.();
        if (swiper) {
          resolve(swiper);
        } else if (Date.now() - startedAt >= timeout) {
          resolve(null);
        } else {
          setTimeout(checkSwiper, 50);
        }
      };
      checkSwiper();
    });
  }

  setupEventListeners() {
    if (!this.swiper) return;

    // Clean up existing observers/listeners if re-initializing
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.handleResizeHandler) {
      window.removeEventListener('resize', this.handleResizeHandler);
    }
    if (this.desktopAutoHeightQuery && this.onDesktopAutoHeightChange) {
      this.desktopAutoHeightQuery.removeEventListener(
        'change',
        this.onDesktopAutoHeightChange
      );
    }

    // Add resize observer to handle container size changes
    if (this.getAttribute('disable-resize-observer') !== 'true') {
      this.resizeObserver = new ResizeObserver(() => {
        this.scheduleLayoutRefresh();
      });

      this.resizeObserver.observe(this.swiper.el);
    }

    this.swiper.on('slideChange', () => {
      this.savedSlideIndex = this.swiper.activeIndex;
      this.dispatchEvent(
        new CustomEvent('swiper:slideChange', {
          detail: { swiper: this.swiper },
        })
      );
    });

    if (this.getAttribute('desktop-auto-height') === 'true') {
      const desktopBreakpoint = `(min-width: ${this.getDesktopBreakpoint()}px)`;
      this.desktopAutoHeightQuery = window.matchMedia(desktopBreakpoint);
      this.onDesktopAutoHeightChange = () => {
        this.scheduleLayoutRefresh();
      };
      this.desktopAutoHeightQuery.addEventListener(
        'change',
        this.onDesktopAutoHeightChange
      );
    }

    this.handleResizeHandler = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResizeHandler);
  }

  scheduleLayoutRefresh() {
    if (this.getAttribute('disableOn')) return;

    clearTimeout(this.layoutRefreshTimer);
    this.layoutRefreshTimer = window.setTimeout(() => {
      this.refreshLayout();
    }, 150);
  }

  refreshLayout() {
    if (!this.swiper || this.getAttribute('disableOn')) return;

    this.syncDesktopAutoHeight();
    this.syncBreakpoint();

    const activeIndex = this.swiper.activeIndex;
    if (typeof this.swiper.updateSize === 'function') {
      this.swiper.updateSize();
    }
    if (typeof this.swiper.updateSlides === 'function') {
      this.swiper.updateSlides();
    }
    this.swiper.update();
    this.swiper.slideTo(activeIndex, 0, false);
  }

  syncBreakpoint() {
    if (!this.swiper?.params?.breakpoints) return;

    if (typeof this.swiper.setBreakpoint === 'function') {
      this.swiper.setBreakpoint();
    }
  }

  syncDesktopAutoHeight() {
    if (this.getAttribute('desktop-auto-height') !== 'true' || !this.swiper) {
      return;
    }

    const isDesktop = window.matchMedia(
      `(min-width: ${this.getDesktopBreakpoint()}px)`
    ).matches;
    const shouldAutoHeight = isDesktop;
    const sliderContainer = this.querySelector('[data-swiper-slideshow]');

    if (this.swiper.params.autoHeight !== shouldAutoHeight) {
      this.swiper.params.autoHeight = shouldAutoHeight;
    }

    if (!shouldAutoHeight) {
      if (this.swiper.wrapperEl) {
        this.swiper.wrapperEl.style.height = '';
      }
      if (sliderContainer) {
        sliderContainer.style.height = '';
        sliderContainer.classList.remove('swiper-autoheight');
      }
      return;
    }

    if (typeof this.swiper.updateAutoHeight === 'function') {
      this.swiper.updateAutoHeight(0);
    }
  }

  handleResize() {
    this.scheduleLayoutRefresh();
  }

  destroySwiperInstance() {
    if (!this.swiper) return;

    this.swiper.destroy(true, true);
    this.swiper = null;
    this.isInitialized = false;
  }

  isSwiperChromeElement(element) {
    return (
      element.classList.contains('swiper-slideshow__navigation') ||
      element.classList.contains('swiper-slideshow__pagination') ||
      element.classList.contains('swiper-scrollbar')
    );
  }

  getSlideElements(container) {
    const wrapper = container.querySelector('.swiper-wrapper');
    if (wrapper) {
      return Array.from(wrapper.children);
    }

    return Array.from(container.children).filter(
      child => !this.isSwiperChromeElement(child)
    );
  }

  unwrapSwiperStructure(container) {
    container.classList.remove('swiper', 'swiper-initialized');
    const wrapper = container.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    const slides = this.getSlideElements(container);
    slides.forEach(slide => {
      slide.classList.remove('swiper-slide');
      container.appendChild(slide);
    });
    wrapper.remove();
  }

  wrapSwiperStructure(container) {
    container.classList.add('swiper');

    let wrapper = container.querySelector('.swiper-wrapper');
    if (wrapper) {
      this.getSlideElements(container).forEach(slide => {
        slide.classList.add('swiper-slide');
      });
      return;
    }

    wrapper = document.createElement('div');
    wrapper.className = 'swiper-wrapper';

    const slides = this.getSlideElements(container);
    slides.forEach(slide => {
      slide.classList.add('swiper-slide');
      wrapper.appendChild(slide);
    });

    const firstChromeElement = Array.from(container.children).find(child =>
      this.isSwiperChromeElement(child)
    );
    if (firstChromeElement) {
      container.insertBefore(wrapper, firstChromeElement);
    } else {
      container.appendChild(wrapper);
    }
  }

  removeGeneratedElements(container) {
    container
      .querySelectorAll(
        '.swiper-slideshow__navigation, .swiper-slideshow__pagination, .swiper-scrollbar'
      )
      .forEach(element => element.remove());
  }

  createNavigationElements(container) {
    const existingNav =
      container.querySelector('.swiper-slideshow__navigation') ||
      this.querySelector(':scope > .swiper-slideshow__navigation');

    if (existingNav) return;

    // Create navigation container
    const navigation = document.createElement('div');
    navigation.className = 'swiper-slideshow__navigation';

    // Create prev button
    const prevButton = document.createElement('button');
    prevButton.type = 'button';
    prevButton.className =
      'swiper-slideshow__nav-button swiper-slideshow__nav-button--prev';
    prevButton.setAttribute('aria-label', 'Previous slide');
    prevButton.innerHTML = this.getChevronLeftIcon();

    // Create next button
    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className =
      'swiper-slideshow__nav-button swiper-slideshow__nav-button--next';
    nextButton.setAttribute('aria-label', 'Next slide');
    nextButton.innerHTML = this.getChevronRightIcon();

    // Add buttons to navigation
    navigation.appendChild(prevButton);
    navigation.appendChild(nextButton);

    // Add navigation after the swiper-wrapper
    const wrapper = container.querySelector('.swiper-wrapper');
    if (wrapper) {
      wrapper.parentNode.insertBefore(navigation, wrapper.nextSibling);
    }
  }

  createScrollbarElement(container) {
    if (container.querySelector('.swiper-scrollbar')) return;

    const scrollbar = document.createElement('div');
    scrollbar.className = 'swiper-scrollbar';

    // Add scrollbar after the swiper-wrapper
    const wrapper = container.querySelector('.swiper-wrapper');
    if (wrapper) {
      wrapper.parentNode.insertBefore(scrollbar, wrapper.nextSibling);
    }
  }

  createPaginationElement(container) {
    if (container.querySelector('.swiper-slideshow__pagination')) return;

    const pagination = document.createElement('div');
    pagination.className = 'swiper-slideshow__pagination';

    const navigation = container.querySelector('.swiper-slideshow__navigation');
    if (navigation) {
      navigation.parentNode.insertBefore(pagination, navigation.nextSibling);
    } else {
      const wrapper = container.querySelector('.swiper-wrapper');
      if (wrapper) {
        wrapper.parentNode.insertBefore(pagination, wrapper.nextSibling);
      }
    }
  }

  getChevronLeftIcon() {
    return window.theme?.renderSlideshowArrowIcon?.('left') || '←';
  }

  getChevronRightIcon() {
    return window.theme?.renderSlideshowArrowIcon?.('right') || '→';
  }

  // Public methods
  getSwiper() {
    return this.swiper;
  }

  slideNext() {
    this.swiper?.slideNext();
  }

  slidePrev() {
    this.swiper?.slidePrev();
  }

  slideTo(index) {
    this.swiper?.slideTo(index);
  }

  startAutoplay() {
    this.swiper?.autoplay?.start();
  }

  stopAutoplay() {
    this.swiper?.autoplay?.stop();
  }

  destroy() {
    this.destroySwiperInstance();
  }

  refresh() {
    this.refreshLayout();
  }

  async reinitialize() {
    if (this.getAttribute('disableOn') || this.shouldUseDesktopLayout()) return;

    this.savedSlideIndex = this.swiper?.activeIndex ?? this.savedSlideIndex ?? 0;

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.desktopAutoHeightQuery && this.onDesktopAutoHeightChange) {
      this.desktopAutoHeightQuery.removeEventListener(
        'change',
        this.onDesktopAutoHeightChange
      );
      this.desktopAutoHeightQuery = null;
      this.onDesktopAutoHeightChange = null;
    }

    if (this.handleResizeHandler) {
      window.removeEventListener('resize', this.handleResizeHandler);
      this.handleResizeHandler = null;
    }

    clearTimeout(this.layoutRefreshTimer);
    this.layoutRefreshTimer = null;

    this.destroySwiperInstance();
    await this.initializeSwiper();
  }
}

if (!customElements.get('swiper-slideshow')) {
  customElements.define('swiper-slideshow', SwiperSlideshow);
}
